import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import DataDashboard from '../../../common/DataDashboard';
import { API_ENDPOINTS } from '../../../../utils/config';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import RegionSelector, { Region } from '../../../common/RegionSelector';
import CompanySelector from '../../../common/CompanySelector';
import { getCategoryName } from '../../../../utils/categories';

/**
 * ProductDetailsDashboard Component
 * 
 * Displays detailed information about products and their supplier relationships.
 * This dashboard shows which suppliers sell which products and at what prices.
 * 
 * @param user - Current authenticated user
 * @param currentRoute - Current route path
 * @param onNavigate - Navigation handler function
 * @param onLogout - Logout handler function
 */
interface ProductDetailsDashboardProps {
  user: any;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
  companyId?: string; // Optional company ID for company-specific view
  layoutType?: 'admin' | 'user'; // Layout type for DataDashboard
  secondarySidebar?: React.ReactNode; // Optional secondary sidebar
  getDynamicSubItems?: (parentId: string, currentRoute: string) => Array<{
    id: string;
    label: string;
    route: string;
    icon?: string;
  }> | undefined;
  navigationActions?: React.ReactNode;
}

const ProductDetailsDashboard: React.FC<ProductDetailsDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout,
  companyId,
  layoutType,
  secondarySidebar,
  getDynamicSubItems,
  navigationActions
}) => {
  const { token } = useAuth();
  const [supplierProducts, setSupplierProducts] = useState<any[]>([]);
  const [productDetails, setProductDetails] = useState<any>(null);
  const [editedProductDetails, setEditedProductDetails] = useState<any>(null);
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [pendingImageUpload, setPendingImageUpload] = useState<{ file: File, base64: string } | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });
  const [isEditing, setIsEditing] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectedRegion, setSelectedRegion] = useState<Region>('tr');
  const [selectedCompanies, setSelectedCompanies] = useState<Array<{ company_id: string; company_name: string }>>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [companyName, setCompanyName] = useState<string>('');

  // Determine effective companyId: prop > user.company_id (for useradmin)
  const effectiveCompanyId = companyId || user?.company_id || null;

  // Load company info if effectiveCompanyId is provided
  useEffect(() => {
    const loadCompanyInfo = async () => {
      if (!effectiveCompanyId || !token) return;

      try {
        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/companies/${effectiveCompanyId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            const company = { company_id: effectiveCompanyId, company_name: data.data.name };
            setCompanyName(data.data.name);
            setSelectedCompanies([company]);
            // If company has a region, enforce it
            if (data.data.region) {
              setSelectedRegion(data.data.region);
            }
            // NOTE: We no longer auto-set isFiltered=true here.
            // This allows all products to be shown, with the company context for adding codes.
          }
        }
      } catch (error) {
        console.error('Error loading company info:', error);
      }
    };

    loadCompanyInfo();
  }, [effectiveCompanyId, token]);

  // NOTE: Removed the auto-filter logic that was hiding products.
  // Now all supplier products are shown. The company context is used for:
  // 1. Pre-selecting the company when adding new supplier products
  // 2. Displaying which company codes exist
  // The "Apply Filter" button can still be used to manually filter if desired.

  // Debug: Log selectedCompanies changes
  useEffect(() => {
    console.log('selectedCompanies state changed:', selectedCompanies);
  }, [selectedCompanies]);

  // Re-apply filter when supplierProducts data changes and filter is active
  useEffect(() => {
    if (isFiltered && selectedCompanies.length > 0 && supplierProducts.length > 0) {
      const filtered = supplierProducts.filter(row => {
        const companyCodes = row.company_specific_codes || [];
        return selectedCompanies.some(selectedCompany =>
          companyCodes.some((code: any) => code.company_id === selectedCompany.company_id && code.code)
        );
      });
      setFilteredData(filtered);
    }
  }, [supplierProducts, isFiltered, selectedCompanies]);

  // Add New Supplier Product Modal States
  const [showAddModal, setShowAddModal] = useState(false);

  // Close all dropdowns when modal opens
  useEffect(() => {
    if (showAddModal) {
      // Close any open CompanySelector dropdowns by dispatching a click event outside
      const clickEvent = new MouseEvent('mousedown', {
        bubbles: true,
        cancelable: true,
        view: window
      });
      document.body.dispatchEvent(clickEvent);
    }
  }, [showAddModal]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [newSupplierProduct, setNewSupplierProduct] = useState({
    supplier_id: '',
    product_id: '',
    company_specific_codes: [] as Array<{ company_id: string; company_name: string; code: string }>,
    price: '',
    currency: 'TRY',
    delivery_time: '',
    minimum_order: '',
    payment_terms: '',
    notes: '',
    is_available: true
  });
  const [supplierProductError, setSupplierProductError] = useState<string | null>(null);

  // Get product_id from URL if present
  const urlParams = new URLSearchParams(window.location.search);
  const productIdFromUrl = urlParams.get('product_id');

  // Load suppliers for dropdown
  const loadSuppliers = async () => {
    try {
      if (!token) return;

      // Backend automatically filters by company based on user's token
      // No need to send company_id - it's enforced server-side for security
      const response = await fetch(`${API_ENDPOINTS.SUPPLIERS_V2}?limit=1000&region=${selectedRegion}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();

      if (result.success) {
        setSuppliers(result.data || []);
        console.log(`✅ Loaded ${result.data?.length || 0} suppliers (filtered by backend)`);
      }
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  // Load products for dropdown
  const loadProducts = async () => {
    try {
      if (!token) return;

      const response = await fetch(`${API_ENDPOINTS.PRODUCTS_V2}?limit=1000&region=${selectedRegion}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();

      if (result.success) {
        setProducts(result.data || []);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  // Load companies for dropdown
  const loadCompanies = async () => {
    try {
      if (!token) return;

      const response = await fetch(`${API_ENDPOINTS.COMPANIES}?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();

      if (result.success) {
        setCompanies(result.data || []);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  // Load supplier products data
  const loadSupplierProducts = async (
    page: number = 1,
    limit: number = 25,
    search: string = '',
    sortBy: string = '_id',
    sortOrder: string = 'desc',
    columnFilters: Record<string, string> = {},
    region: Region = 'tr'
  ) => {
    try {
      setIsLoading(true);
      setError(null);

      if (!token) {
        setError('Authentication token not found');
        return;
      }

      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
        region: region
      });

      if (search) {
        params.append('search', search);
      }

      // Add column filters
      Object.entries(columnFilters).forEach(([key, value]) => {
        if (value.trim()) {
          params.append(`filter_${key}`, value);
        }
      });

      // If product_id is in URL, add it as a filter
      if (productIdFromUrl) {
        // The productIdFromUrl is actually the _id of the product, not product_id field
        // So we need to find supplier_products that have this product_id
        params.append('filter_product_id', productIdFromUrl);
        console.log(`🔍 FRONTEND DEBUG: Filtering by product_id: ${productIdFromUrl}`);
      } else {
        console.log(`🔍 FRONTEND DEBUG: No product_id filter, showing all supplier products`);
      }

      const finalUrl = `${API_ENDPOINTS.SUPPLIER_PRODUCTS_V2}?${params}`;
      console.log(`🔍 FRONTEND DEBUG: Request URL: ${finalUrl}`);

      const response = await fetch(finalUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setSupplierProducts(result.data || []);
        setPaginationInfo(result.pagination || paginationInfo);
      } else {
        throw new Error(result.error || 'Failed to load supplier products');
      }

    } catch (error) {
      console.error('Error loading supplier products:', error);
      setError(error instanceof Error ? error.message : 'Failed to load supplier products');
    } finally {
      setIsLoading(false);
    }
  };

  // Load product details
  const loadProductDetails = async (productId: string) => {
    try {
      if (!token || !productId) {
        return;
      }

      const response = await fetch(`${API_ENDPOINTS.PRODUCTS_V2}/${productId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        const productData = data.data;

        // Generate image_url from image_gridfs_id if it exists
        if (productData.image_gridfs_id && !productData.image_url) {
          productData.image_url = `${API_ENDPOINTS.BASE_URL.replace('/api', '')}/api/gridfs/${productData.image_gridfs_id}`;
        }

        setProductDetails(productData);
        setEditedProductDetails(productData); // Initialize edited state
      }
    } catch (error) {
      console.error('Error loading product details:', error);
    }
  };

  // Handle product field edit (local state only)
  const handleProductFieldEdit = (field: string, value: any) => {
    setEditedProductDetails((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  // Save all changes to backend
  const handleSaveProductChanges = async () => {
    setIsSaving(true);
    try {
      if (!token || !productDetails || !editedProductDetails) {
        alert('Missing required data. Please refresh and try again.');
        return;
      }

      // Find changed fields
      const changes: any = {};
      Object.keys(editedProductDetails).forEach(key => {
        if (editedProductDetails[key] !== productDetails[key]) {
          changes[key] = editedProductDetails[key];
        }
      });

      // Add image upload if pending
      if (pendingImageUpload) {
        // Use cropped image if available, otherwise use original
        const imageToUpload = croppedImage || pendingImageUpload.base64;
        changes.image_file = imageToUpload;
        changes.image_filename = pendingImageUpload.file.name;
        console.log('🔍 DEBUG: Adding pending image upload to changes', croppedImage ? '(cropped)' : '(original)');
        console.log('🔍 DEBUG: Image size (chars):', imageToUpload?.length);
      }

      if (Object.keys(changes).length === 0 && !pendingImageUpload) {
        // No changes to save
        setIsEditingProduct(false);
        return;
      }

      console.log('🔍 DEBUG: Saving changes:', Object.keys(changes));

      const response = await fetch(`${API_ENDPOINTS.PRODUCTS_V2}/${productDetails._id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(changes)
      });

      console.log('🔍 DEBUG: Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 DEBUG: Error response:', errorText);
        alert(`Error saving changes: ${response.status} - ${errorText || 'Unknown error'}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('🔍 DEBUG: Save result:', result);

      if (result.success) {
        // Update main state with edited values
        setProductDetails(editedProductDetails);
        // Clear pending image upload and crop states
        setPendingImageUpload(null);
        setCrop(undefined);
        setCompletedCrop(undefined);
        setCroppedImage(null);
        setIsEditingProduct(false);

        // Reload product details to get new image URL if image was uploaded
        if (pendingImageUpload) {
          loadProductDetails(productDetails._id);
        }
      } else {
        alert(`Save failed: ${result.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error saving product changes:', error);
      alert(`Error saving: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle image upload (local state only - no backend upload)
  const handleImageUpload = (file: File) => {
    try {
      console.log('🔍 DEBUG: handleImageUpload called with file:', file.name, file.type, file.size);

      // Check file type - support modern image formats
      const supportedTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/tiff',
        'image/webp', 'image/avif', 'image/heic', 'image/heif', 'image/svg+xml'
      ];

      console.log('🔍 DEBUG: File type:', file.type, 'Supported:', supportedTypes.includes(file.type.toLowerCase()));

      if (!supportedTypes.includes(file.type.toLowerCase())) {
        alert('Please select a supported image file (JPEG, PNG, GIF, WebP, AVIF, HEIC, SVG)');
        return;
      }

      console.log('🔍 DEBUG: Starting file read...');

      // Convert file to base64 and store in local state
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        console.log('🔍 DEBUG: Base64 length:', base64.length);

        // Store pending image upload
        setPendingImageUpload({
          file: file,
          base64: base64
        });

        // Reset crop state
        setCrop(undefined);
        setCompletedCrop(undefined);
        setCroppedImage(null);

        console.log('🔍 DEBUG: Image stored in local state, will upload on save');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('🔍 DEBUG: Error processing image:', error);
    }
  };

  // Crop image function
  const onImageLoad = (img: HTMLImageElement) => {
    imgRef.current = img;
    const { width, height } = img;
    // Set initial crop to cover most of the image (90%)
    const cropWidth = width * 0.9;
    const cropHeight = height * 0.9;
    const initialCrop = {
      unit: 'px' as const,
      x: (width - cropWidth) / 2,
      y: (height - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight
    };
    setCrop(initialCrop);
    // Set as completed crop so it's ready to apply immediately
    setCompletedCrop(initialCrop);
  };

  // Apply crop
  const applyCrop = () => {
    if (!completedCrop || !imgRef.current) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const image = imgRef.current;

    // Get the actual image dimensions (not the displayed size)
    const { naturalWidth, naturalHeight } = image;
    const { width: displayWidth, height: displayHeight } = image.getBoundingClientRect();

    // Calculate scale factors
    const scaleX = naturalWidth / displayWidth;
    const scaleY = naturalHeight / displayHeight;

    // Convert crop coordinates to natural image coordinates
    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;
    const cropWidth = completedCrop.width * scaleX;
    const cropHeight = completedCrop.height * scaleY;

    console.log('🔍 DEBUG: Crop info:', {
      naturalWidth,
      naturalHeight,
      displayWidth,
      displayHeight,
      scaleX,
      scaleY,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      completedCrop
    });

    // Set canvas size to crop dimensions
    canvas.width = cropWidth;
    canvas.height = cropHeight;

    // Draw the cropped portion
    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

    const croppedBase64 = canvas.toDataURL();

    // Debug: Create a temporary image to check cropped dimensions
    const tempImg = new Image();
    tempImg.onload = () => {
      console.log('🔍 DEBUG: Cropped image actual dimensions:', tempImg.width, 'x', tempImg.height);
    };
    tempImg.src = croppedBase64;

    setCroppedImage(croppedBase64);
    console.log('🔍 DEBUG: Cropped image created');
  };

  // Reset crop
  const resetCrop = () => {
    setCrop(undefined);
    setCompletedCrop(undefined);
    setCroppedImage(null);
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isEditingProduct) {
      setIsDragOver(true);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isEditingProduct) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (!isEditingProduct) {
      return;
    }

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      handleImageUpload(file);
    }
  };

  // Handle add supplier product
  const handleAddSupplierProduct = async () => {
    // Load suppliers, products, and companies
    await Promise.all([loadSuppliers(), loadProducts(), loadCompanies()]);

    // Determine default product ID
    let defaultProductId = productIdFromUrl || '';
    if (!defaultProductId && productDetails) {
      defaultProductId = productDetails._id || '';
    }

    // Determine default company codes
    let defaultCompanyCodes: any[] = [];
    if (effectiveCompanyId) {
      defaultCompanyCodes.push({
        company_id: effectiveCompanyId,
        company_name: companyName || 'Current Company',
        code: '1' // Default code for this company
      });
    }

    console.log('🔍 DEBUG: handleAddSupplierProduct defaults:', {
      defaultProductId,
      defaultCompanyCodes,
      productIdFromUrl,
      effectiveCompanyId,
      companyName
    });

    // Initialize with defaults
    setNewSupplierProduct({
      supplier_id: '',
      product_id: defaultProductId,
      company_specific_codes: defaultCompanyCodes,
      price: '',
      currency: 'TRY', // Default currency
      delivery_time: '',
      minimum_order: '',
      payment_terms: '',
      notes: '',
      is_available: true
    });

    // Open modal
    setShowAddModal(true);
  };

  // Handle adding company code to existing supplier product
  const handleAddCompanyCodeToExisting = async (existingId: string, existingCodes: any[]) => {
    try {
      console.log('🔍 DEBUG: Smart update - Adding company code to existing relationship:', existingId);

      // Check if user's company code already exists
      // We use effectiveCompanyId which is what the backend uses for validation
      const userCompanyId = effectiveCompanyId;

      if (!userCompanyId) {
        setSupplierProductError('Cannot add company code: Your company ID is missing');
        return;
      }

      const hasUserCompanyCode = existingCodes?.some(
        (code: any) => code.company_id === userCompanyId
      );

      if (hasUserCompanyCode) {
        setSupplierProductError('Your company already has a code for this supplier-product relationship');
        return;
      }

      // Filter out empty codes from new input
      const newCodesToAdd = newSupplierProduct.company_specific_codes.filter(c => c.code && c.code.trim() !== '');

      if (newCodesToAdd.length === 0) {
        // If user selected company but didn't enter code, we still want to add the company with default '1'
        // But the backend will handle that logic if we send it.
        // However, here we are patching.
        // Let's assume we want to add the code for the current company from the form.
        const userCompanyCode = newSupplierProduct.company_specific_codes.find(c => c.company_id === userCompanyId);
        if (userCompanyCode) {
          newCodesToAdd.push(userCompanyCode);
        }
      }

      // Check if we have codes to add
      if (newCodesToAdd.length === 0) {
        // If no specific code entered, create a default one for the user's company
        if (companyName) {
          newCodesToAdd.push({
            company_id: userCompanyId,
            company_name: companyName,
            code: '1'
          });
        }
      }

      // Combine existing and new codes
      const updatedCodes = [
        ...(existingCodes || []),
        ...newCodesToAdd
      ];

      console.log('🔍 DEBUG: Patching with updated codes:', updatedCodes);

      // PATCH the existing supplier-product
      const response = await fetch(`${API_ENDPOINTS.SUPPLIER_PRODUCTS_V2}/${existingId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          company_specific_codes: updatedCodes
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update existing relationship: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        alert('Successfully added your company code to the existing supplier-product relationship');

        // Close modal and reset form
        setShowAddModal(false);
        setSupplierProductError(null);
        setNewSupplierProduct({
          supplier_id: '',
          product_id: '',
          company_specific_codes: [],
          price: '',
          currency: 'TRY',
          delivery_time: '',
          minimum_order: '',
          payment_terms: '',
          notes: '',
          is_available: true
        });

        // Reload data
        loadSupplierProducts(paginationInfo.page, paginationInfo.limit, '', '_id', 'desc', {}, selectedRegion);
      } else {
        setSupplierProductError(result.error || 'Failed to update existing relationship');
      }

    } catch (error) {
      console.error('Error updating existing supplier product:', error);
      setSupplierProductError('Failed to update existing relationship');
    }
  };

  // Submit new supplier product
  const handleSubmitSupplierProduct = async () => {
    try {
      setSupplierProductError(null); // Clear previous errors

      if (!token) return;

      // Validate required fields
      if (!newSupplierProduct.supplier_id || !newSupplierProduct.product_id) {
        setSupplierProductError('Please select both supplier and product');
        return;
      }

      if (!newSupplierProduct.price) {
        setSupplierProductError('Please enter a price');
        return;
      }

      const requestData = {
        ...newSupplierProduct,
        price: parseFloat(newSupplierProduct.price),
        region: selectedRegion
      };

      console.log('Sending data to backend:', requestData);

      const response = await fetch(API_ENDPOINTS.SUPPLIER_PRODUCTS_V2, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      const result = await response.json();

      if (!response.ok) {
        const errorMessage = result.error || 'Unknown error';
        console.error('Backend error:', result);

        // If relationship exists, try to add company code instead
        // Check for specific error message or if backend returns existing_id
        if (response.status === 400 && (result.existing_id || errorMessage.includes('already exists'))) {
          if (result.existing_id) {
            await handleAddCompanyCodeToExisting(result.existing_id, result.existing_company_codes);
            return;
          } else {
            setSupplierProductError('This supplier-product relationship already exists. Please check the list.');
            return;
          }
        }

        setSupplierProductError(errorMessage);
        return;
      }

      if (result.success) {
        // Close modal and reset form
        setShowAddModal(false);
        setSupplierProductError(null);
        setNewSupplierProduct({
          supplier_id: '',
          product_id: '',
          company_specific_codes: [],
          price: '',
          currency: 'TRY',
          delivery_time: '',
          minimum_order: '',
          payment_terms: '',
          notes: '',
          is_available: true
        });

        // Reload data
        loadSupplierProducts(paginationInfo.page, paginationInfo.limit, '', '_id', 'desc', {}, selectedRegion);
      }
    } catch (error) {
      console.error('Error creating supplier product:', error);
      setSupplierProductError('Failed to create supplier product: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  // Handle data fetch callback from DataDashboard
  const handleDataFetch = (params: {
    page: number;
    limit: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    columnFilters?: Record<string, string>;
  }) => {
    loadSupplierProducts(
      params.page,
      params.limit,
      params.search || '',
      params.sortBy || '_id',
      params.sortOrder || 'desc',
      params.columnFilters || {},
      selectedRegion
    );
  };

  // Handle edit supplier product
  const handleEditSupplierProduct = async (id: string, field: string, value: any, row?: any) => {
    try {
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Map display fields to actual database fields
      let actualField = field;
      let actualValue = value;

      // If editing supplier_name, we need to send supplier_id (ObjectId)
      if (field === 'supplier_name') {
        actualField = 'supplier_id';
        actualValue = value; // value is already ObjectId from dropdown
      }

      // If editing product_name, we need to send product_id (ObjectId)
      if (field === 'product_name') {
        actualField = 'product_id';
        actualValue = value; // value is already ObjectId from dropdown
      }

      // If editing a company code field
      if (field.startsWith('company_code_')) {
        const companyId = field.replace('company_code_', '');
        const company = selectedCompanies.find(c => c.company_id === companyId);

        console.log('🔧 Editing company code:', { field, companyId, company, value, row });

        if (company && row) {
          // Get current company_specific_codes array (make a copy)
          let companyCodes = JSON.parse(JSON.stringify(row.company_specific_codes || []));

          console.log('🔧 Current company codes:', companyCodes);

          // Find existing code for this company
          const existingCodeIndex = companyCodes.findIndex((code: any) => code.company_id === companyId);

          console.log('🔧 Existing code index:', existingCodeIndex);

          if (value && value.trim() !== '') {
            // Add or update the code
            if (existingCodeIndex >= 0) {
              // Update existing code
              companyCodes[existingCodeIndex].code = value.trim();
              console.log('🔧 Updated existing code');
            } else {
              // Add new code
              companyCodes.push({
                company_id: companyId,
                company_name: company.company_name,
                code: value.trim()
              });
              console.log('🔧 Added new code');
            }
          } else {
            // Remove the code if value is empty
            if (existingCodeIndex >= 0) {
              companyCodes.splice(existingCodeIndex, 1);
              console.log('🔧 Removed code');
            }
          }

          console.log('🔧 Final company codes to send:', companyCodes);

          // Send the updated array to backend
          actualField = 'company_specific_codes';
          actualValue = companyCodes;
        }
      }

      const requestBody = { [actualField]: actualValue };
      console.log('🔧 Sending PATCH request:', { id, actualField, actualValue, requestBody });

      const response = await fetch(API_ENDPOINTS.SUPPLIER_PRODUCT_DETAIL(id), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log('🔧 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔧 Backend error response:', errorText);
        try {
          const errorJson = JSON.parse(errorText);
          console.error('🔧 Backend error JSON:', errorJson);
        } catch (e) {
          console.error('🔧 Could not parse error as JSON');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('🔧 Success response:', result);

      if (result.success) {
        // Refresh data to show updated supplier/product names and IDs
        loadSupplierProducts();
      } else {
        throw new Error(result.error || 'Failed to update supplier product');
      }

    } catch (error) {
      console.error('Error updating supplier product:', error);
      throw error;
    }
  };

  // Handle delete supplier products
  const handleDeleteSupplierProducts = async (ids: string[]) => {
    try {
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Delete each supplier product individually
      for (const id of ids) {
        const response = await fetch(API_ENDPOINTS.SUPPLIER_PRODUCT_DETAIL(id), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Failed to delete supplier product');
        }
      }

      // Refresh data
      loadSupplierProducts();

    } catch (error) {
      console.error('Error deleting supplier products:', error);
      throw error;
    }
  };

  // Handle export
  const handleExport = async (exportData: any) => {
    try {
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Prepare the data for export
      const dataToExport = (exportData.filters && Object.keys(exportData.filters).length > 0
        ? supplierProducts.filter(item => exportData.filters._id?.$in?.includes(item._id))
        : supplierProducts
      ).map(item => {
        const exportItem: any = {};

        // Map selected fields
        exportData.fields.forEach((field: string) => {
          if (field.startsWith('company_code_')) {
            // Handle company code fields
            const companyId = field.replace('company_code_', '');
            const companyCodes = item.company_specific_codes || [];
            const companyCode = companyCodes.find((code: any) => code.company_id === companyId);
            exportItem[field] = companyCode?.code || '';
          } else {
            // Regular fields
            exportItem[field] = item[field];
          }
        });

        return exportItem;
      });

      const response = await fetch(API_ENDPOINTS.EXPORT_DATA, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          collection: 'supplier_products',
          data: dataToExport,
          fields: exportData.fields,
          format: exportData.format || 'excel',
          filename: exportData.filename || `supplier-products-${new Date().toISOString().split('T')[0]}`
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      // Format: isim_YYYYMMDD
      const today = new Date();
      const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
      a.download = `${exportData.filename || 'supplier-products'}_${dateStr}.${exportData.format === 'csv' ? 'csv' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('Error exporting supplier products:', error);
      throw error;
    }
  };

  // Handle edit toggle
  const handleEditToggle = async () => {
    if (!isEditing) {
      // Load suppliers, products, and companies when entering edit mode
      await Promise.all([loadSuppliers(), loadProducts(), loadCompanies()]);
    }
    setIsEditing(!isEditing);
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    setIsEditing(false);
    // Refresh data to show saved changes
    loadSupplierProducts();
  };

  // Handle selection change
  const handleSelectionChange = (selectedItems: string[]) => {
    setSelectedProducts(selectedItems);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    loadSupplierProducts(1, newItemsPerPage, '', '_id', 'desc', {}, selectedRegion);
  };

  useEffect(() => {
    if (token) {
      loadSupplierProducts(1, 25, '', '_id', 'desc', {}, selectedRegion);
      // Load product details if product_id is in URL
      if (productIdFromUrl) {
        loadProductDetails(productIdFromUrl);
      }
    }
  }, [token, productIdFromUrl, selectedRegion, selectedCompanies]);


  // Create header content for product details
  const productHeaderContent = productDetails && (
    <div className="product-details-header" style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '20px',
      padding: '20px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      marginBottom: '20px',
      border: '1px solid #e9ecef',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      {/* Product Information */}
      <div className="product-info" style={{
        flex: 1,
        minWidth: 0,
        overflow: 'hidden'
      }}>
        {/* Action Buttons Row - Above Title */}
        {layoutType === 'admin' && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button
              onClick={() => {
                if (isEditingProduct) {
                  handleSaveProductChanges();
                } else {
                  setIsEditingProduct(true);
                }
              }}
              disabled={isSaving}
              style={{
                padding: '8px 20px',
                backgroundColor: isSaving ? '#969696' : '#4292c6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isSaving ? 0.8 : 1
              }}
            >
              {isSaving ? 'Saving...' : (isEditingProduct ? 'Save' : 'Edit')}
            </button>
            {isEditingProduct && !isSaving && (
              <button
                onClick={() => {
                  setIsEditingProduct(false);
                  setEditedProductDetails(productDetails);
                  setPendingImageUpload(null);
                  setCrop(undefined);
                  setCompletedCrop(undefined);
                  setCroppedImage(null);
                }}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#737373',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            )}
          </div>
        )}

        {/* Product Title */}
        <h2 style={{
          margin: '0 0 12px 0',
          fontSize: '24px',
          fontWeight: '600',
          color: '#212529',
          wordBreak: 'break-word'
        }}>
          {isEditingProduct ? (
            <input
              type="text"
              value={editedProductDetails?.name || ''}
              onChange={(e) => handleProductFieldEdit('name', e.target.value)}
              style={{
                fontSize: '20px',
                fontWeight: '600',
                border: '1px solid #4292c6',
                borderRadius: '4px',
                padding: '6px 10px',
                width: '100%',
                maxWidth: '500px'
              }}
            />
          ) : (
            productDetails.name
          )}
        </h2>

        {/* Vertical layout for product details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <strong style={{ color: '#495057', fontSize: '14px', minWidth: '140px', display: 'inline-block' }}>Product ID:</strong>
            <span style={{ color: '#212529', fontSize: '14px', fontWeight: '500' }}>{productDetails.product_id || 'N/A'}</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <strong style={{ color: '#495057', fontSize: '14px', minWidth: '140px', display: 'inline-block' }}>Brand:</strong>
            {isEditingProduct ? (
              <input
                type="text"
                value={editedProductDetails?.brand || ''}
                onChange={(e) => handleProductFieldEdit('brand', e.target.value)}
                style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '2px 6px', fontSize: '14px' }}
              />
            ) : (
              <span style={{ color: '#212529', fontSize: '14px' }}>{productDetails.brand || 'N/A'}</span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <strong style={{ color: '#495057', fontSize: '14px', minWidth: '140px', display: 'inline-block' }}>Category:</strong>
            {isEditingProduct ? (
              <input
                type="text"
                value={editedProductDetails?.category || ''}
                onChange={(e) => handleProductFieldEdit('category', e.target.value)}
                style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '2px 6px', fontSize: '14px' }}
              />
            ) : (
              (() => {
                const value = productDetails.category;
                if (!value) return <span style={{ color: '#212529', fontSize: '14px' }}>N/A</span>;

                let displayStr = value;

                // specialized parsing logic for concatenated categories
                let parsedCategoryIds: string[] = [];
                const asString = String(value);

                // Check for 8-digit sequences which are likely category IDs
                const matches = asString.match(/\d{8}/g);

                if (matches && matches.length > 0) {
                  parsedCategoryIds = matches;

                  // Map IDs to names
                  const names = parsedCategoryIds.map(id => {
                    const name = getCategoryName(id, 'tr');
                    // If name === id, mapping failed.
                    return name;
                  });

                  // If we found valid names that are different from IDs, use them
                  if (names.some(n => n !== parsedCategoryIds[names.indexOf(n)])) {
                    displayStr = names.join(', ');
                  }
                }

                return <span style={{ color: '#212529', fontSize: '14px' }}>{displayStr}</span>;
              })()
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <strong style={{ color: '#495057', fontSize: '14px', minWidth: '140px', display: 'inline-block' }}>Global Code:</strong>
            {isEditingProduct ? (
              <input
                type="text"
                value={editedProductDetails?.global_code || ''}
                onChange={(e) => handleProductFieldEdit('global_code', e.target.value)}
                style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '2px 6px', fontSize: '14px' }}
              />
            ) : (
              <span style={{ color: '#212529', fontSize: '14px' }}>{productDetails.global_code || 'N/A'}</span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <strong style={{ color: '#495057', fontSize: '14px', minWidth: '140px', display: 'inline-block' }}>Amazon ASIN:</strong>
            {isEditingProduct ? (
              <input
                type="text"
                value={editedProductDetails?.amazon_asin || ''}
                onChange={(e) => handleProductFieldEdit('amazon_asin', e.target.value)}
                style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '2px 6px', fontSize: '14px' }}
              />
            ) : (
              <span style={{ color: '#212529', fontSize: '14px' }}>{productDetails.amazon_asin || 'N/A'}</span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <strong style={{ color: '#495057', fontSize: '14px', minWidth: '140px', display: 'inline-block' }}>Dimensions:</strong>
            {isEditingProduct ? (
              <input
                type="text"
                value={editedProductDetails?.dimensions || ''}
                onChange={(e) => handleProductFieldEdit('dimensions', e.target.value)}
                style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '2px 6px', fontSize: '14px' }}
              />
            ) : (
              <span style={{ color: '#212529', fontSize: '14px' }}>{productDetails.dimensions || 'N/A'}</span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <strong style={{ color: '#495057', fontSize: '14px', minWidth: '140px', display: 'inline-block' }}>Base Unit:</strong>
            {isEditingProduct ? (
              <input
                type="text"
                value={editedProductDetails?.base_unit || ''}
                onChange={(e) => handleProductFieldEdit('base_unit', e.target.value)}
                style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '2px 6px', fontSize: '14px' }}
              />
            ) : (
              <span style={{ color: '#212529', fontSize: '14px' }}>{productDetails.base_unit || 'N/A'}</span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <strong style={{ color: '#495057', fontSize: '14px', minWidth: '140px', display: 'inline-block' }}>Color:</strong>
            {isEditingProduct ? (
              <input
                type="text"
                value={editedProductDetails?.color || ''}
                onChange={(e) => handleProductFieldEdit('color', e.target.value)}
                style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '2px 6px', fontSize: '14px' }}
              />
            ) : (
              <span style={{ color: '#212529', fontSize: '14px' }}>{productDetails.color || 'N/A'}</span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <strong style={{ color: '#495057', fontSize: '14px', minWidth: '140px', display: 'inline-block' }}>Description:</strong>
            {isEditingProduct ? (
              <textarea
                value={editedProductDetails?.description || ''}
                onChange={(e) => handleProductFieldEdit('description', e.target.value)}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '4px 6px',
                  fontSize: '14px',
                  width: '300px',
                  height: '60px',
                  resize: 'vertical'
                }}
              />
            ) : (
              <span style={{ color: '#212529', fontSize: '14px' }}>{productDetails.description || 'N/A'}</span>
            )}
          </div>

          {layoutType === 'admin' && (
            <div style={{ display: 'flex', alignItems: 'baseline' }}>
              <strong style={{ color: '#495057', fontSize: '14px', minWidth: '140px', display: 'inline-block' }}>Status:</strong>
              {isEditingProduct ? (
                <select
                  value={editedProductDetails?.is_active ? 'true' : 'false'}
                  onChange={(e) => handleProductFieldEdit('is_active', e.target.value === 'true')}
                  style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '2px 6px', fontSize: '14px' }}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              ) : (
                <span style={{
                  color: productDetails.is_active ? '#28a745' : '#dc3545',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {productDetails.is_active ? 'Active' : 'Inactive'}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Product Image */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
        <div
          className="product-image product-image-large"
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            width: '280px !important',
            height: '280px !important',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: isDragOver ? '#f8f9fa' : '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: isDragOver ? '2px dashed #007bff' : '2px solid #e9ecef',
            flexShrink: 0,
            minWidth: '280px !important',
            minHeight: '280px !important',
            maxWidth: '280px !important',
            maxHeight: '280px !important',
            cursor: isEditingProduct ? 'pointer' : 'default',
            transition: 'all 0.2s ease'
          }}
        >
          {isDragOver && isEditingProduct ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 123, 255, 0.1)',
              color: '#007bff',
              fontSize: '16px',
              fontWeight: '600',
              textAlign: 'center'
            }}>
              Drop image here
            </div>
          ) : pendingImageUpload ? (
            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
              {croppedImage ? (
                // Show cropped preview - show actual crop size
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '280px',
                  minHeight: '280px',
                  maxWidth: '600px', // Increased to allow larger images
                  maxHeight: '600px', // Increased to allow larger images
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  padding: '10px'
                }}>
                  <img
                    src={croppedImage}
                    alt={productDetails.name}
                    onLoad={(e) => {
                      const img = e.currentTarget;
                      console.log('🔍 DEBUG: Preview image dimensions:', img.naturalWidth, 'x', img.naturalHeight);
                      console.log('🔍 DEBUG: Preview display size:', img.width, 'x', img.height);
                    }}
                    style={{
                      width: 'auto',
                      height: 'auto'
                      // No maxWidth/maxHeight - let image show at natural size
                    }}
                  />
                </div>
              ) : crop ? (
                // Show crop interface
                <div style={{
                  width: '280px',
                  height: '280px',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    onComplete={(c) => setCompletedCrop(c)}
                    // aspect={1} // Removed to allow free resizing
                    style={{ width: '100%', height: '100%' }}
                  >
                    <img
                      ref={imgRef}
                      src={pendingImageUpload.base64}
                      alt={productDetails.name}
                      onLoad={(e) => onImageLoad(e.currentTarget)}
                      style={{
                        maxWidth: '280px',
                        maxHeight: '280px',
                        objectFit: 'contain'
                      }}
                    />
                  </ReactCrop>
                </div>
              ) : (
                // Show original image
                <img
                  src={pendingImageUpload.base64}
                  alt={productDetails.name}
                  onLoad={(e) => onImageLoad(e.currentTarget)}
                  style={{
                    maxWidth: '280px',
                    maxHeight: '280px',
                    objectFit: 'contain'
                  }}
                />
              )}
            </div>
          ) : productDetails.image_url ? (
            <img
              src={productDetails.image_url}
              alt={productDetails.name}
              style={{
                width: '100% !important',
                height: '100% !important',
                objectFit: 'contain',
                maxWidth: '280px !important',
                maxHeight: '280px !important',
                minWidth: '280px !important',
                minHeight: '280px !important'
              }}
            />
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              backgroundColor: '#f8f9fa',
              color: '#6c757d',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              No Image
            </div>
          )}
        </div>

        {isEditingProduct && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
            <label style={{
              backgroundColor: 'transparent',
              color: '#495057',
              border: '1px solid #6c757d',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}>
              {pendingImageUpload ? 'Change Pending Image' : (productDetails.image_url ? 'Change Image' : 'Upload Image')}
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/bmp,image/tiff,image/webp,image/avif,image/heic,image/heif,image/svg+xml"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageUpload(file);
                }}
                style={{ display: 'none' }}
              />
            </label>

            {pendingImageUpload && (crop || croppedImage) && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {crop && !croppedImage && (
                  <button
                    onClick={applyCrop}
                    style={{
                      backgroundColor: 'transparent',
                      color: '#495057',
                      border: '1px solid #6c757d',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Apply Crop
                  </button>
                )}
                {croppedImage && (
                  <button
                    onClick={() => {
                      setCroppedImage(null);
                      // Don't reset crop, keep the current crop area for editing
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      color: '#495057',
                      border: '1px solid #6c757d',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Edit Crop
                  </button>
                )}
                <button
                  onClick={resetCrop}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#495057',
                    border: '1px solid #6c757d',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Reset Crop
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Combined region selector and company filter header content
  const regionAndCompanyHeaderContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem', position: 'relative', overflow: 'visible' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        {/* Only show RegionSelector if NOT in a specific company view */}
        {!companyId && (
          <RegionSelector value={selectedRegion} onChange={setSelectedRegion} />
        )}

        {/* Hide CompanySelector if companyId is provided */}
        {!companyId && (
          <div style={{ position: 'relative', zIndex: 10001, display: 'inline-block' }}>
            <CompanySelector
              value={selectedCompanies}
              onChange={(companies) => {
                console.log('Company selection changed:', companies);
                setSelectedCompanies(companies);
              }}
              multiple={true}
              className="company-filter-selector"
              placeholder="Companies"
            />
          </div>
        )}

        <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>
          Viewing supplier products from: <strong>{selectedRegion.toUpperCase()}</strong>
        </span>
        {!companyId && selectedCompanies.length > 0 && (
          <button
            onClick={() => {
              // Filter data to show only rows with selected company codes
              console.log('Filtering by companies:', selectedCompanies);
              const filtered = supplierProducts.filter(row => {
                const companyCodes = row.company_specific_codes || [];
                return selectedCompanies.some(selectedCompany =>
                  companyCodes.some((code: any) => code.company_id === selectedCompany.company_id && code.code)
                );
              });
              console.log('Filtered data:', filtered);
              setFilteredData(filtered);
              setIsFiltered(true);
            }}
            style={{
              padding: '5px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: '#ffffff',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
          >
            Apply Filter
          </button>
        )}
        {!companyId && isFiltered && (
          <button
            onClick={() => {
              setIsFiltered(false);
              setFilteredData([]);
            }}
            style={{
              padding: '5px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: '#ffffff',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
          >
            Clear Filter
          </button>
        )}
      </div>

      {/* Selected Companies Display */}
      {!companyId && selectedCompanies.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          padding: '8px 12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #e9ecef'
        }}>
          {selectedCompanies.map((company) => (
            <span
              key={company.company_id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 8px',
                backgroundColor: '#e0f2fe',
                border: '1px solid #bae6fd',
                borderRadius: '4px',
                fontSize: '12px',
                color: '#0c4a6e',
                fontWeight: '500'
              }}
            >
              {company.company_name}
              {/* Only show remove button in global view */}
              {!companyId && (
                <button
                  onClick={() => {
                    setSelectedCompanies(selectedCompanies.filter(c => c.company_id !== company.company_id));
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#0369a1',
                    cursor: 'pointer',
                    fontSize: '16px',
                    lineHeight: '1',
                    padding: '0',
                    marginLeft: '2px'
                  }}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  // Generate export fields including company codes
  const exportFields = useMemo(() => {
    const baseFields = [
      { name: 'supplier_product_id', type: 'text', description: 'SP ID' },
      { name: 'supplier_id_string', type: 'text', description: 'Supplier ID' },
      { name: 'supplier_name', type: 'text', description: 'Supplier Name' },
      { name: 'product_id_string', type: 'text', description: 'Product ID' },
      { name: 'product_name', type: 'text', description: 'Product Name' },
      { name: 'product_brand', type: 'text', description: 'Brand' },
      { name: 'price', type: 'number', description: 'Price' },
      { name: 'currency', type: 'text', description: 'Currency' },
      { name: 'delivery_time', type: 'text', description: 'Delivery Time' },
      { name: 'minimum_order', type: 'text', description: 'Min Order' },
      { name: 'payment_terms', type: 'text', description: 'Payment Terms' },
      { name: 'is_available', type: 'boolean', description: 'Available' },
      { name: 'last_price_update', type: 'date', description: 'Last Update' }
    ];

    // Add company-specific code fields if companies are available
    if (companies && companies.length > 0) {
      companies.forEach(company => {
        baseFields.push({
          name: `company_code_${company._id}`,
          type: 'text',
          description: `${company.name} Code`
        });
      });
    }

    return baseFields;
  }, [companies]);

  // Generate dynamic columns based on selected companies
  const generateColumns = useMemo(() => {
    console.log('Generating columns with selectedCompanies:', selectedCompanies);
    const baseColumns = [
      { key: 'supplier_product_id', title: 'SP ID', type: 'text' as const, width: '100px', editable: false, sortable: true },
      { key: 'supplier_id_string', title: 'Supplier ID', type: 'text' as const, width: '110px', editable: false, sortable: true },
      {
        key: 'supplier_name',
        title: 'Supplier Name',
        type: 'text' as const,
        width: '150px',
        editable: layoutType === 'admin', // Only admin can edit
        sortable: true,
        options: suppliers.map(s => ({ value: s._id, label: `${s.supplier_id} - ${s.name}` })),
        render: (value: any, row: any) => {
          // Display the actual supplier name from row data
          return row.supplier_name || 'N/A';
        }
      },
      { key: 'product_id_string', title: 'Product ID', type: 'text' as const, width: '110px', editable: false, sortable: true },
      {
        key: 'product_name',
        title: 'Product Name',
        type: 'text' as const,
        width: '150px',
        editable: layoutType === 'admin', // Only admin can edit
        sortable: true,
        options: products.map(p => ({ value: p._id, label: `${p.product_id} - ${p.name}` })),
        render: (value: any, row: any) => {
          // Display the actual product name from row data
          return row.product_name || 'N/A';
        }
      },
      { key: 'product_brand', title: 'Brand', type: 'text' as const, width: '120px', editable: false, sortable: true }
    ];

    // Add company-specific columns for selected companies
    const companyColumns = selectedCompanies.map(company => {
      return {
        key: `company_code_${company.company_id}`,
        title: `${company.company_name} Code`,
        type: 'text' as const,
        width: '150px',
        editable: true,
        sortable: false,
        // Store the company info in the column for later use
        companyInfo: company,
        render: (value: any, row: any) => {
          const companyCodes = row.company_specific_codes || [];
          const companyCode = companyCodes.find((code: any) => code.company_id === company.company_id);
          return companyCode?.code || '';
        }
      };
    });

    // console.log('Generated companyColumns:', companyColumns);

    // Add remaining columns
    const remainingColumns = [
      { key: 'price', title: 'Price', type: 'number' as const, width: '100px', editable: layoutType === 'admin', sortable: true },
      { key: 'currency', title: 'Currency', type: 'dropdown' as const, width: '80px', editable: layoutType === 'admin', sortable: true, options: ['EUR', 'USD', 'TRY', 'CHF', 'GBP'] },
      {
        key: 'delivery_time',
        title: 'Delivery Time',
        type: 'text' as const,
        width: '120px',
        editable: layoutType === 'admin',
        sortable: true,
        render: (value: any) => value ? `${value} day(s)` : ''
      },
      { key: 'minimum_order', title: 'Min Order', type: 'text' as const, width: '100px', editable: layoutType === 'admin', sortable: true },
      {
        key: 'payment_terms',
        title: 'Payment Terms',
        type: 'text' as const,
        width: '120px',
        editable: layoutType === 'admin',
        sortable: true,
        render: (value: any) => value ? `${value} day(s)` : ''
      },
      // Only show is_available for admin
      ...(layoutType === 'admin' ? [{ key: 'is_available', title: 'Available', type: 'status' as const, width: '100px', editable: true, sortable: true }] : []),
      { key: 'last_price_update', title: 'Last Update', type: 'date' as const, width: '120px', editable: false, sortable: true }
    ];

    const finalColumns = [...baseColumns, ...companyColumns, ...remainingColumns];
    // console.log('Final columns:', finalColumns);
    return finalColumns;
  }, [selectedCompanies, suppliers, products]);

  // Combine product details header with region selector and company filter if product details exist
  const combinedHeaderContent = (
    <div style={{ position: 'relative' }}>
      {regionAndCompanyHeaderContent}
      {productHeaderContent}
    </div>
  );

  return (
    <>
      {/* Add New Supplier Product Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10002
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '24px', fontSize: '20px', fontWeight: '600' }}>
              Add Supplier to the Product
            </h2>

            {/* Error Display */}
            {supplierProductError && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                color: '#856404',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '18px' }}>⚠️</span>
                <div>{supplierProductError}</div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Supplier Dropdown */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Supplier <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  value={newSupplierProduct.supplier_id}
                  onChange={(e) => setNewSupplierProduct({ ...newSupplierProduct, supplier_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select Supplier</option>
                  {suppliers.map(supplier => (
                    <option key={supplier._id} value={supplier._id}>
                      {supplier.supplier_id} - {supplier.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Product Dropdown */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Product <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  value={newSupplierProduct.product_id}
                  onChange={(e) => setNewSupplierProduct({ ...newSupplierProduct, product_id: e.target.value })}
                  disabled={!!productIdFromUrl}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: productIdFromUrl ? '#f0f0f0' : 'white'
                  }}
                >
                  <option value="">Select Product</option>
                  {/* Ensure current product is available even if not in the list (e.g. inactive) */}
                  {productDetails && !products.find(p => p._id === productDetails._id) && (
                    <option key={productDetails._id} value={productDetails._id}>
                      {productDetails.product_id} - {productDetails.name} {productDetails.status !== 'active' ? `(${productDetails.status || 'unverified'})` : ''}
                    </option>
                  )}
                  {products.map(product => (
                    <option key={product._id} value={product._id}>
                      {product.product_id} - {product.name}
                    </option>
                  ))}
                </select>

                {/* Determine the currently selected product object for status checking */}
                {(() => {
                  // For URL-based product selection, use productDetails which has the is_active field
                  // products list from dropdown may not include is_active
                  let selectedProduct: any = null;
                  if (productIdFromUrl && productDetails && productDetails._id === newSupplierProduct.product_id) {
                    selectedProduct = productDetails;
                  } else {
                    // Fallback to products list, but prefer productDetails if matching
                    const fromProducts = products.find(p => p._id === newSupplierProduct.product_id);
                    selectedProduct = fromProducts ||
                      (productDetails && productDetails._id === newSupplierProduct.product_id ? productDetails : null);
                  }

                  // Debug logging
                  console.log('DEBUG: Admin Warning Check', {
                    layoutType,
                    selectedProduct,
                    is_active: selectedProduct?.is_active,
                    productDetailsIsActive: productDetails?.is_active,
                    isInactive: selectedProduct && selectedProduct.is_active === false
                  });

                  // Only show warning if product exists and is_active is false
                  // Note: Using is_active field, not status field
                  const isInactive = selectedProduct && selectedProduct.is_active === false;

                  return layoutType === 'admin' && isInactive && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffeeba',
                      borderRadius: '4px',
                      color: '#856404',
                      fontSize: '13px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>⚠️</span>
                        <strong>Product is Inactive</strong>
                      </div>
                      <div>
                        This product needs approval before it appears in standard lists.
                        {' '}
                        <button
                          onClick={() => {
                            setShowAddModal(false);
                            onNavigate(`/dashboard/products?search=${selectedProduct.product_id}`);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: '#0056b3',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                        >
                          Click to approve
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Company Specific Codes */}
              <div style={{ position: 'relative' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Company Specific Codes (Optional)
                </label>
                {companyId ? (
                  <div style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: '#e9ecef',
                    color: '#495057'
                  }}>
                    {companyName || 'Current Company'}
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    {/* Dropdown Trigger */}
                    <div
                      onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <span style={{ color: newSupplierProduct.company_specific_codes.length > 0 ? '#333' : '#999' }}>
                        {newSupplierProduct.company_specific_codes.length > 0
                          ? `${newSupplierProduct.company_specific_codes.length} company selected`
                          : 'Select companies...'}
                      </span>
                      <span style={{ fontSize: '10px', color: '#666' }}>{showCompanyDropdown ? '▲' : '▼'}</span>
                    </div>

                    {/* Dropdown Menu */}
                    {showCompanyDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 10,
                        marginTop: '4px'
                      }}>
                        {companies.map((company: any) => {
                          const isSelected = newSupplierProduct.company_specific_codes.some(c => c.company_id === company._id);
                          return (
                            <div
                              key={company._id}
                              onClick={() => {
                                if (isSelected) {
                                  // Remove company
                                  setNewSupplierProduct({
                                    ...newSupplierProduct,
                                    company_specific_codes: newSupplierProduct.company_specific_codes.filter(c => c.company_id !== company._id)
                                  });
                                } else {
                                  // Add company
                                  setNewSupplierProduct({
                                    ...newSupplierProduct,
                                    company_specific_codes: [
                                      ...newSupplierProduct.company_specific_codes,
                                      { company_id: company._id, company_name: company.name, code: '' }
                                    ]
                                  });
                                }
                              }}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                borderBottom: '1px solid #eee'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isSelected ? '#bbdefb' : '#f5f5f5'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isSelected ? '#e3f2fd' : 'white'}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => { }}
                                style={{ cursor: 'pointer' }}
                              />
                              <span style={{ fontSize: '14px' }}>{company.name}</span>
                            </div>
                          );
                        })}
                        {companies.length === 0 && (
                          <div style={{ padding: '12px', color: '#999', textAlign: 'center' }}>
                            No companies available
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Show code inputs for selected companies */}
                {newSupplierProduct.company_specific_codes.length > 0 && (
                  <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {newSupplierProduct.company_specific_codes.map((companyCode, index) => (
                      <div key={companyCode.company_id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '500', minWidth: '150px' }}>
                          {companyCode.company_name} Code:
                        </span>
                        <input
                          type="text"
                          value={companyCode.code}
                          onChange={(e) => {
                            const newCodes = [...newSupplierProduct.company_specific_codes];
                            newCodes[index].code = e.target.value;
                            setNewSupplierProduct({ ...newSupplierProduct, company_specific_codes: newCodes });
                          }}
                          placeholder={`Enter code for ${companyCode.company_name}`}
                          style={{
                            flex: 1,
                            padding: '6px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Price */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                    Price <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="number"
                    value={newSupplierProduct.price}
                    onChange={(e) => setNewSupplierProduct({ ...newSupplierProduct, price: e.target.value })}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                    Currency
                  </label>
                  <select
                    value={newSupplierProduct.currency}
                    onChange={(e) => setNewSupplierProduct({ ...newSupplierProduct, currency: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="TRY">TRY</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>

              {/* Delivery Time & Minimum Order */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                    Delivery Time (days)
                  </label>
                  <div style={{ position: 'relative', overflow: 'hidden' }}>
                    <input
                      type="number"
                      min="0"
                      value={newSupplierProduct.delivery_time}
                      onChange={(e) => setNewSupplierProduct({ ...newSupplierProduct, delivery_time: e.target.value })}
                      placeholder="Enter days"
                      style={{
                        width: 'calc(100% - 55px)',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                    <span style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#6c757d',
                      fontSize: '13px',
                      pointerEvents: 'none',
                      whiteSpace: 'nowrap'
                    }}>
                      day(s)
                    </span>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                    Minimum Order
                  </label>
                  <input
                    type="text"
                    value={newSupplierProduct.minimum_order}
                    onChange={(e) => setNewSupplierProduct({ ...newSupplierProduct, minimum_order: e.target.value })}
                    placeholder="e.g. 100 pieces"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              {/* Payment Terms */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Payment Terms (days)
                </label>
                <div style={{ position: 'relative', overflow: 'hidden' }}>
                  <input
                    type="number"
                    min="0"
                    value={newSupplierProduct.payment_terms}
                    onChange={(e) => setNewSupplierProduct({ ...newSupplierProduct, payment_terms: e.target.value })}
                    placeholder="Enter days"
                    style={{
                      width: 'calc(100% - 55px)',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                  <span style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6c757d',
                    fontSize: '13px',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap'
                  }}>
                    day(s)
                  </span>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Notes
                </label>
                <textarea
                  value={newSupplierProduct.notes}
                  onChange={(e) => setNewSupplierProduct({ ...newSupplierProduct, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    resize: 'vertical',
                    marginBottom: '160px'
                  }}
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSupplierProductError(null);
                  setNewSupplierProduct({
                    supplier_id: '',
                    product_id: '',
                    company_specific_codes: [],
                    price: '',
                    currency: 'TRY',
                    delivery_time: '',
                    minimum_order: '',
                    payment_terms: '',
                    notes: '',
                    is_available: true
                  });
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitSupplierProduct}
                disabled={(() => {
                  // Use same logic as warning: prioritize productDetails for status
                  let selectedProduct = null;
                  if (productIdFromUrl && productDetails && productDetails._id === newSupplierProduct.product_id) {
                    selectedProduct = productDetails;
                  } else {
                    const fromProducts = products.find(p => p._id === newSupplierProduct.product_id);
                    selectedProduct = fromProducts ||
                      (productDetails && productDetails._id === newSupplierProduct.product_id ? productDetails : null);
                  }
                  return layoutType === 'admin' && selectedProduct && selectedProduct.is_active === false;
                })()}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: (() => {
                    let selectedProduct = null;
                    if (productIdFromUrl && productDetails && productDetails._id === newSupplierProduct.product_id) {
                      selectedProduct = productDetails;
                    } else {
                      const fromProducts = products.find(p => p._id === newSupplierProduct.product_id);
                      selectedProduct = fromProducts ||
                        (productDetails && productDetails._id === newSupplierProduct.product_id ? productDetails : null);
                    }
                    return layoutType === 'admin' && selectedProduct && selectedProduct.is_active === false ? '#d6d8db' : '#007bff';
                  })(),
                  color: 'white',
                  cursor: (() => {
                    let selectedProduct = null;
                    if (productIdFromUrl && productDetails && productDetails._id === newSupplierProduct.product_id) {
                      selectedProduct = productDetails;
                    } else {
                      const fromProducts = products.find(p => p._id === newSupplierProduct.product_id);
                      selectedProduct = fromProducts ||
                        (productDetails && productDetails._id === newSupplierProduct.product_id ? productDetails : null);
                    }
                    return layoutType === 'admin' && selectedProduct && selectedProduct.is_active === false ? 'not-allowed' : 'pointer';
                  })(),
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Add Supplier Product
              </button>
            </div>
          </div>
        </div>
      )}

      <DataDashboard
        title="Product Details"
        pageDescription="Add Supplier to the Product"
        data={isFiltered ? filteredData : supplierProducts}
        collection="supplier_products"
        isLoading={isLoading}
        error={error}
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        onDataChange={() => { }} // Not used in this context
        showSearch={true}
        showAddButton={true}
        addButtonLabel="Add Supplier to the Product"
        showSelection={true}
        onAdd={handleAddSupplierProduct}
        getDynamicSubItems={getDynamicSubItems}
        navigationActions={navigationActions}
        onDataFetch={handleDataFetch}
        paginationInfo={paginationInfo}
        isEditing={isEditing}
        onEditToggle={handleEditToggle}
        onSaveChanges={handleSaveChanges}
        selectedItems={selectedProducts}
        onSelectionChange={handleSelectionChange}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onEdit={handleEditSupplierProduct}
        onDelete={handleDeleteSupplierProducts}
        onExport={handleExport}
        customExportFields={exportFields}
        columns={generateColumns}
        headerContent={combinedHeaderContent}
        layoutType={layoutType}
        secondarySidebar={secondarySidebar}
      />
    </>
  );
};

export default ProductDetailsDashboard;